import { Circle, Container, Graphics, Sprite, Text, Texture } from 'pixi.js';
import { IRoomSprite, IToken, L } from 'src/sync/_api';
import { Room } from 'src/sync/room';
import { Store } from 'src/sync/store';
import { RoomSprite } from './room-sprite';
import { Mouse } from './mouse';
import { Gfx } from './gfx';
import { Settings } from './settings';
import { Account } from 'src/sync/account';
import { booleanPointInPolygon, point } from '@turf/turf'
import { DropTokenMessage } from 'src/app/messenger-service';

export class Token extends RoomSprite {
    public uncertainAsset = false;
    public override type: 'token' | 'image' = 'token';
    private tokenDowntime = Date.now();
    private textBackground = new Graphics();

    private textLabel = new Text({ text: this.data.label });
    private healthbar = this.createHealthbar();
    private healthLabel = new Text({ text: this.data.health })
    private shield = new Sprite(Gfx.textures['shield']);
    private shieldLabel = new Text({ text: this.data.armor });
    private selection = new Graphics();

    constructor(public override data: IToken, public override sprite: Sprite, private container: Container, private label: Container) {
        super(data as IRoomSprite, sprite);
        sprite.anchor.set(0.5, 0.5);
        const trashcan = Mouse.erasing && Gfx.activeLayer.id == L.Tokens;
        sprite.cursor = trashcan ? 'url(./assets/trashcan.png) 16 16, auto' : 'all-scroll';
        sprite.on('pointerup', () => {
            if (Date.now() - this.tokenDowntime < 150) {
                Mouse.cancel();
                this.select(true);
            }
        });
        sprite.on('pointerdown', (e) => {
            if (e.button == 0) {
                if (Mouse.erasing && Gfx.activeLayer.id == L.Tokens) {
                    this.deselect();
                    Room.document.transact(() => Room.tokens.delete(this.data.id), L.Tokens);
                    Mouse.dragTarget = undefined;
                    setTimeout(() => Mouse.cancel());
                } else {
                    this.tokenDowntime = Date.now();
                }
            };
        });

        this.container.addChildAt(this.selection, 0);
        this.label.addChild(this.textBackground, this.textLabel);
        this.textBackground.tint = '0x000000';
        this.textLabel.anchor.set(0, -0.15);
        this.textLabel.style.fontFamily = 'Arial';
        this.textLabel.style.fontSize = 90;
        this.textLabel.style.fill = '0xffffff';
        this.container.addChild(this.shield);
        this.container.addChild(this.shieldLabel);
        this.container.addChild(this.healthbar);
        this.container.addChild(this.healthLabel);
        this.healthbar.mask = this.container.addChild(new Graphics());

        this.textBackground.eventMode = 'none';
        this.textLabel.eventMode = 'none';
        this.shield.eventMode = 'none';
        this.shieldLabel.eventMode = 'none';
        this.shieldLabel.style.fontFamily = 'Fantasy';
        this.shieldLabel.style.fontSize = 60;
        this.shieldLabel.style.fill = '0xffffff';
        this.healthbar.eventMode = 'none';
        this.healthbar.mask.eventMode = 'none';
        this.healthLabel.style.fontFamily = 'Fantasy';
        this.healthLabel.style.fontSize = 40;
        this.healthLabel.style.fill = '0x000000';

        this.update(this.data);
    }

    public redraw() {
        // Do nothing
    }

    public override select(activate: boolean) {
        const selectorId = Room.tokens.get(this.data.id).selectorId;
        const locked = selectorId != undefined && selectorId !== Account.id;

        if (locked && Store.isReferee.value == false) return;

        if (locked) {
            Room.sendStateless(new DropTokenMessage({ tokenId: this.data.id }, Account.id, selectorId));
        }

        super.select(activate);
        Room.tokens.modify(this.data.id, x => x.selectorId = Account.id);
        this.drawSelection();
    }

    public override deselect() {
        const selectorId = Room.tokens.get(this.data.id).selectorId;
        if (selectorId == Account.id) {
            super.deselect();
            Room.tokens.modify(this.data.id, x => x.selectorId = undefined);
            this.drawSelection();
        }
    }

    public override destroy() {
        super.destroy();
        this.container.destroy();
        this.label.destroy();
    }

    public emitChanges() {
        Room.tokens.set(this.data.id, this.data);
    }

    public update(data: IToken) {
        this.data = data;
        this.sprite.rotation = data.rotation;
        this.sprite.width = 100 * Math.max(data.scale.x, 0.2);
        this.sprite.height = 100 * Math.max(data.scale.y, 0.2);
        this.sprite.hitArea = new Circle(0, 0, this.sprite.getLocalBounds().width / 2);
        this.sprite.position.copyFrom(data.position);
        this.drawLabel();
        this.drawHealthbar();
        this.drawShield();
        this.drawSelection();
        this.updateVisibility(this.data.visible);
    }

    public updateVisibility(show: boolean) {
        this.sprite.alpha = show ? 1 : (Store.isReferee.value ? 0.6 : 0);
        if (Store.isReferee.value == false) {
            const fogPolygon = Gfx.layers[L.Fog].inverted;
            const center = point([this.sprite.x, this.sprite.y])
            const corner1 = point([this.sprite.x - (this.sprite.width / 2), this.sprite.y - (this.sprite.height / 2)])
            const corner2 = point([this.sprite.x + (this.sprite.width / 2), this.sprite.y - (this.sprite.height / 2)])
            const corner3 = point([this.sprite.x + (this.sprite.width / 2), this.sprite.y + (this.sprite.height / 2)])
            const corner4 = point([this.sprite.x - (this.sprite.width / 2), this.sprite.y + (this.sprite.height / 2)])

            if (!booleanPointInPolygon(center, fogPolygon) && !booleanPointInPolygon(corner1, fogPolygon) && !booleanPointInPolygon(corner2, fogPolygon) &&
                !booleanPointInPolygon(corner3, fogPolygon) && !booleanPointInPolygon(corner4, fogPolygon)) {
                this.sprite.alpha = 0;
            }
        }

        // If we're NOT a referee and token is NOT visible, deselect it
        if (Store.isReferee.value == false && this.sprite.alpha == 0) {
            Store.activeSprite.next(undefined);
        }

        this.sprite.eventMode = this.sprite.alpha == 0 ? 'none' : 'static';
        this.label.alpha = this.sprite.alpha;
        this.shield.alpha = this.sprite.alpha;
        this.shieldLabel.alpha = this.sprite.alpha;
        this.selection.alpha = this.sprite.alpha;
        this.healthLabel.alpha = this.sprite.alpha;
        this.healthbar.alpha = this.sprite.alpha == 1 ? 0.85 : this.sprite.alpha;
    }

    private drawSelection() {
        this.selection.clear();
        if (this.data.selectorId == null) return;
        const color = Room.attendance.get(this.data.selectorId).color;

        this.selection.circle(0, 0, 1.05 * (this.sprite.width / 2)).fill(color);
        this.selection.position.copyFrom(this.sprite);
    }

    // TODO: Simplify this to toggle this.label visibility now that the field is no longer public
    private drawLabel() {
        this.textLabel.text = this.data.label;
        if (this.data.label == null || this.data.label == '') {
            this.textLabel.alpha = 0;
            this.textBackground.alpha = 0;
        } else {
            this.textBackground.alpha = 0.5;
            this.textLabel.alpha = 1;
        }

        this.label.scale.set(1);
        const backgroundMargin = 20;
        this.textBackground.clear().roundRect(-backgroundMargin, 0, this.textLabel.width + 40, this.textLabel.height + 10, backgroundMargin).fill();
        this.label.position.copyFrom(this.sprite.position);
        this.label.pivot.set((this.textBackground.width / 2) - backgroundMargin, this.textBackground.height / 2);
        const maxLabelScale = 0.15 * (this.sprite.width / Settings.gridOffset);
        const scale = Math.min(maxLabelScale, this.sprite.width / this.label.width);
        this.label.scale.set(scale);
        this.label.position.y += (this.sprite.height / 2) - (this.label.height / 2);
        this.textLabel.position.y = -10;
    }

    private drawShield(): void {
        this.shieldLabel.text = this.data.armor ?? '';
        if (this.data.armor == null) {
            this.shield.visible = false;
            this.shieldLabel.visible = false;
        } else {
            this.shield.visible = true;
            this.shieldLabel.visible = true;

            this.shield.position.copyFrom(this.sprite.position);
            this.shield.position.x += -(this.sprite.width / 2);
            this.shield.position.y += -(this.sprite.height / 2) + this.healthbar.height;
            this.shield.width = this.sprite.width / 5;
            this.shield.height = this.sprite.height / 5;

            const scale = 0.2 * (this.sprite.width / Settings.gridOffset);
            this.shieldLabel.scale.set(scale);
            this.shieldLabel.position.copyFrom(this.shield.position);
            this.shieldLabel.position.x += ((this.shield.width - (this.shieldLabel.width)) / 2);
            this.shieldLabel.position.y += (((this.shield.height * 0.9) - (this.shieldLabel.height)) / 2);
        }
    }

    private drawHealthbar(): void {
        this.healthbar.width = this.sprite.width * 0.9;
        this.healthbar.height = this.sprite.height * 0.05;
        this.healthbar.position.copyFrom(this.sprite.position);
        this.healthbar.position.x += -(this.sprite.width / 2) + this.sprite.width * 0.05;
        this.healthbar.position.y += -(this.sprite.height / 2);

        const max = (this.data.maxhealth ?? this.data.health ?? 1);
        const total = this.data.health ?? 0;
        const health = Math.min(max, total);

        const percentpoints = this.healthbar.width - (this.healthbar.width * health / max);
        const mask = this.healthbar.mask as Graphics;
        mask.position.copyFrom(this.healthbar.position);

        if (this.data.health == undefined) {
            this.healthbar.visible = false;
            mask.clear().rect(this.healthbar.width, 0, this.healthbar.width, this.healthbar.height).fill({ color: '0x000000' });
            this.healthLabel.text = '';
        } else {
            this.healthbar.visible = true;
            const scale = 0.1 * (this.sprite.width / Settings.gridOffset);
            this.healthLabel.scale.set(scale);
            this.healthLabel.text = total > max ? `${health} + ${total - max}` : health;
            this.healthLabel.position.copyFrom(this.healthbar.position);
            this.healthLabel.position.x += percentpoints + 1;

            if (health <= 0) {
                this.healthbar.tint = '0xFF0000';
                const width = this.healthbar.width / 4;
                const position = (this.healthbar.width / 2) - (width / 2);
                mask.clear().rect(position, 0, width, this.healthbar.height).fill({ color: '0x000000' });

                this.healthLabel.position.copyFrom(this.healthbar.position);
                this.healthLabel.position.x += (this.healthbar.width / 2);
                this.healthLabel.position.x -= (this.healthLabel.width / 2);
                this.healthLabel.style.fill = '0xFFFFFF';
            } else {
                this.healthLabel.style.fill = '0x000000';
                this.healthbar.tint = '0xFFFFFF';
                mask.clear().rect(percentpoints, 0, this.healthbar.width, this.healthbar.height).fill({ color: '0x000000' });
            }
        }
    }

    private createHealthbar(): Sprite {
        const canvas = document.createElement('canvas');
        canvas.width = 20;
        canvas.height = 1;
        const context = canvas.getContext('2d') as CanvasRenderingContext2D;
        const gradient = context.createLinearGradient(0, 0, canvas.width, canvas.height);
        gradient.addColorStop(0, '#33cc66');
        gradient.addColorStop(0.8, '#bbcc66');
        gradient.addColorStop(0.9, '#cc9933');
        gradient.addColorStop(1, '#cc4433');
        context.fillStyle = gradient;
        context.fillRect(0, 0, canvas.width, canvas.height);
        const texture = Texture.from(canvas)
        return new Sprite(texture);
    }

    override clone() {
        const clone: IToken = {
            id: crypto.randomUUID(),
            scale: this.data.scale,
            rotation: this.data.rotation,
            position: this.data.position,
            visible: this.data.visible,
            locked: this.data.locked,

            assetId: this.data.assetId,
            label: this.data.label,
            armor: this.data.armor,
            health: this.data.health,
            maxhealth: this.data.maxhealth,
            selectorId: undefined,
        };
        Room.tokens.set(clone.id, clone);
    }
}
